import React, { useEffect, useRef, useState } from "react";
import { Toast, ToastContainer } from "react-bootstrap";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useAuthContext } from "@/context/AuthContext";
import { pushEvent } from "@/lib/gtmDataLayer";
import { consumeOnDemandSetupReturn, ON_DEMAND_SETUP_SUCCESS_PROMPT_KEY, stripOnDemandSetupQuery } from "@/lib/onDemandSetup";
import { setSessionStorageItem } from "@/lib/storageUtils";

/**
 * Global handler for Stripe setup-session returns (`onDemandSetup=success|cancelled`).
 * Mount once under AuthContext so Studio + settings both pick up the redirect.
 */
export default function OnDemandSetupReturnHandler() {
  const t = useTranslations();
  const router = useRouter();
  const {
    refreshBillingState,
    user
  } = useAuthContext();
  const handledRef = useRef<string | null>(null);
  const [toast, setToast] = useState<{
    kind: "success" | "info";
    message: string;
  } | null>(null);
  useEffect(() => {
    if (!router.isReady || !user?.uid) return;
    const result = consumeOnDemandSetupReturn(router.query);
    if (result.status === "none") return;
    const dedupeKey = `${result.status}:${"sessionId" in result ? result.sessionId : ""}`;
    if (handledRef.current === dedupeKey) return;
    handledRef.current = dedupeKey;
    const {
      pathname,
      query
    } = stripOnDemandSetupQuery(router.pathname, router.query);
    void router.replace({
      pathname,
      query
    }, undefined, {
      shallow: true
    });
    if (!result.isNew) return;
    if (result.status === "success") {
      void refreshBillingState({
        force: true
      }).catch(error => {
        console.error("Failed to refresh billing after On Demand setup", error);
      });
      setSessionStorageItem(ON_DEMAND_SETUP_SUCCESS_PROMPT_KEY, "1");
      pushEvent("on_demand_setup_succeeded", {
        session_id: result.sessionId
      });
      setToast({
        kind: "success",
        message: t("billing.onDemand.setupSuccess")
      });
      return;
    }
    pushEvent("on_demand_setup_cancelled", {});
    setToast({
      kind: "info",
      message: t("billing.onDemand.setupCancelled")
    });
  }, [refreshBillingState, router, t, user?.uid]);
  if (!toast) return null;
  return <ToastContainer position="bottom-end" className="p-3" style={{
    zIndex: 2100
  }} data-sentry-element="ToastContainer" data-sentry-component="OnDemandSetupReturnHandler" data-sentry-source-file="OnDemandSetupReturnHandler.tsx">
            <Toast show={Boolean(toast)} onClose={() => setToast(null)} delay={6000} autohide bg={toast.kind === "success" ? "success" : "secondary"} data-sentry-element="Toast" data-sentry-source-file="OnDemandSetupReturnHandler.tsx">
                <Toast.Body className="text-white" data-sentry-element="unknown" data-sentry-source-file="OnDemandSetupReturnHandler.tsx">{toast.message}</Toast.Body>
            </Toast>
        </ToastContainer>;
}